import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { actionCardsApi, tasksApi, contactsApi, organizationApi, teamsApi, templatesApi } from '@/utils/api';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
  DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent,
} from '@/components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ConfidenceBadge, PriorityBadge, StatusBadge } from '@/components/ConfidenceBadge';
import RichNote, { bulletsToHtml, htmlToBullets } from '@/components/RichNote';
import { taskRYG, rygFromTasks, aggregateRYG, RYG_META, RYG_ORDER, todayStr } from '@/utils/ryg';
import { ActivityTimeline } from '@/components/ActivityTimeline';
import { relTime } from '@/utils/time';
import EmailModal from '@/components/EmailModal';
import ShareModal from '@/components/ShareModal';
import { toast } from 'sonner';
import ExportPdfDialog from '@/components/ExportPdfDialog';
import {
  ArrowLeft, Mail, Tag, User, Calendar, Clock,
  MoreHorizontal, Check, Trash2, Zap, Phone,
  ChevronDown, ChevronUp, Share2, Plus, X, Loader2,
  Edit2, AlertCircle, Download, MessageSquare, Bell, BellOff,
  Copy, GitMerge, Hash, Sparkles, Users, LayoutTemplate, FileText, Mic, ArrowRight, MapPin
} from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import InlineTaskPanel from '@/components/InlineTaskPanel';
import { useTaskDrawer } from '@/contexts/TaskDrawerContext';
import { AssigneeAvatar } from '@/components/AssigneeAvatar';
import { FilterSelect } from '@/components/FilterSelect';

// ─── Participant stack ────────────────────────────────────────────────────────
const AV_PALETTE = ['', 'green', 'butter', 'ink', 'bd'];
const NON_PERSON_NAMES = new Set(['unknown', 'unknown contact', 'null', 'none', 'n/a', 'nobody', 'someone']);
function isRealPerson(name) {
  const n = (name || '').trim().toLowerCase();
  return !!n && !NON_PERSON_NAMES.has(n);
}
function getInitials(name) {
  return (name || '?').trim().split(/\s+/).map(w => w[0]).join('').toUpperCase().slice(0, 2);
}

function ParticipantStack({ people, participants, userName, onSave, onOpenContact }) {
  const [adding, setAdding] = useState(false);
  const [val, setVal] = useState('');
  if (!people.length) return null;

  const canEdit = typeof onSave === 'function';
  const removable = new Set(
    (participants || [])
      .filter(p => (p?.name || '').trim() && (p?.role || '').toLowerCase() !== 'recorder')
      .map(p => (p.name || '').trim().toLowerCase())
  );
  const add = () => {
    const c = val.trim();
    if (c && !(participants || []).some(p => (p.name || '').toLowerCase() === c.toLowerCase())) {
      const base = (participants || []).filter(p => (p?.name || '').trim());
      onSave([...base, { name: c, role: 'contact' }]);
    }
    setVal(''); setAdding(false);
  };
  const remove = (name) => onSave((participants || []).filter(p => (p?.name || '').trim() && (p?.name || '') !== name));

  return (
    <div className="people-stack" tabIndex={0} aria-label={`${people.length} participant${people.length !== 1 ? 's' : ''}`} data-testid="action-card-participants">
      {people.map((p, i) => (
        <span key={i} className={`av ${AV_PALETTE[i % AV_PALETTE.length]}`}>{getInitials(p.name)}</span>
      ))}
      <div className="stack-tip" data-testid="action-card-participants-popover">
        <div className="tip-h">{people.length} participant{people.length !== 1 ? 's' : ''}</div>
        {people.map((p, i) => {
          const isYou = p.role === 'You';
          const canRemove = canEdit && removable.has((p.name || '').trim().toLowerCase());
          const clickable = !isYou && typeof onOpenContact === 'function';
          return (
            <div className={`tip-row${clickable ? ' clickable' : ''}`} key={i}
              onClick={clickable ? () => onOpenContact(p.name) : undefined}
              data-testid={`action-card-participant-row-${i}`}>
              <span className={`av ${AV_PALETTE[i % AV_PALETTE.length]}`}>{getInitials(p.name)}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="tn">{p.name}{isYou && <em className="tip-you">you</em>}</div>
                <div className="tr">{p.role}</div>
              </div>
              {canRemove && (
                <button className="tip-x" onClick={(e) => { e.stopPropagation(); remove(p.name); }}
                  aria-label={`Remove ${p.name}`} data-testid={`action-card-participant-remove-${i}`}><X size={12} /></button>
              )}
            </div>
          );
        })}
        {canEdit && (
          <div className="tip-add-row">
            {adding ? (
              <input autoFocus className="ac-part-input" value={val}
                onChange={e => setVal(e.target.value)} onBlur={add}
                onKeyDown={e => { if (e.key === 'Enter') add(); if (e.key === 'Escape') { setVal(''); setAdding(false); } }}
                placeholder="Attendee name" data-testid="action-card-participant-input" />
            ) : (
              <button className="ac-part-add" onClick={() => setAdding(true)} data-testid="action-card-add-participant-button"><Plus size={12} />Add participant</button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}



// ─── Transcript Viewer ───────────────────────────────────────────────────────
// Formats speaker-labelled transcript, expandable to full height
function TranscriptViewer({ transcript, speakerMap = {} }) {
  const [expanded, setExpanded] = useState(false);

  const labelFor = (letter) => speakerMap[letter] || `Speaker ${letter}`;

  const renderLine = (line, i) => {
    const match = line.match(/^Speaker ([A-Z]): (.+)$/);
    if (match) {
      return (
        <div key={i} className="tx-line">
          <span className="tx-sp">{labelFor(match[1])}</span>
          <span className="tx-txt">{match[2]}</span>
        </div>
      );
    }
    return <p key={i} className="tx-txt" style={{ padding: '3px 0' }}>{line}</p>;
  };

  const lines = transcript.split('\n').filter(Boolean);
  const previewLines = lines.slice(0, 6);
  const hasMore = lines.length > 6;

  return (
    <section className="card" data-testid="action-card-transcript">
      <div className={`tx-head${expanded ? ' open' : ''}`} onClick={() => setExpanded(p => !p)}>
        <div className="tx-l">Transcript<span className="tx-c">{lines.length} lines</span></div>
        <button className="tx-toggle">
          {expanded ? 'Collapse' : 'View full transcript'}
          <ChevronDown size={13} style={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
        </button>
      </div>
      <div className="tx-body">
        {(expanded ? lines : previewLines).map((line, i) => renderLine(line, i))}
        {!expanded && hasMore && (
          <button className="tx-more" onClick={() => setExpanded(true)}>+ {lines.length - 6} more lines — click to expand</button>
        )}
        {expanded && (
          <button className="tx-more" onClick={() => setExpanded(false)}>↑ Collapse transcript</button>
        )}
      </div>
    </section>
  );
}


// ─── Inline editable field ───────────────────────────────────────────────────
function InlineField({ value, onSave, placeholder, className, type = 'text', multiline = false }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const inputRef = useRef(null);

  const autoSize = () => {
    const el = inputRef.current;
    if (el && multiline) { el.style.height = 'auto'; el.style.height = `${el.scrollHeight}px`; }
  };

  useEffect(() => {
    if (editing) {
      const el = inputRef.current;
      el?.focus();
      if (el) {
        el.setSelectionRange(el.value.length, el.value.length);
        if (multiline) { el.style.height = 'auto'; el.style.height = `${el.scrollHeight}px`; }
      }
    }
  }, [editing, multiline]);

  const start = () => { setDraft(value || ''); setEditing(true); };
  const save = () => {
    setEditing(false);
    if (draft.trim() !== (value || '')) onSave(draft.trim());
  };
  const cancel = () => { setEditing(false); setDraft(value || ''); };

  if (editing) {
    if (multiline) {
      return (
        <textarea
          ref={inputRef}
          value={draft}
          onChange={e => { setDraft(e.target.value); autoSize(); }}
          onBlur={save}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); save(); }
            if (e.key === 'Escape') cancel();
          }}
          rows={1}
          className={`tk-edit tk-edit-area ${className || ''}`}
          placeholder={placeholder}
          data-testid="task-title-textarea"
        />
      );
    }
    return (
      <input
        ref={inputRef}
        type={type}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={save}
        onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') cancel(); }}
        className={`tk-edit ${className || ''}`}
        style={{ minWidth: '70px' }}
        placeholder={placeholder}
      />
    );
  }

  return (
    <span onClick={start} className={`tk-edit txt ${!value ? 'empty' : ''} ${className || ''}`} title="Click to edit">
      {value || placeholder || '—'}
    </span>
  );
}

// ─── Priority cycle button ────────────────────────────────────────────────────
function PriorityCycle({ value, onSave }) {
  const cycle = { high: 'medium', medium: 'low', low: 'high' };
  const labels = { high: 'High', medium: 'Med', low: 'Low' };
  const v = value || 'medium';
  return (
    <button className={`prio ${v}`} onClick={() => onSave(cycle[v] || 'medium')} title="Click to change priority">
      {labels[v]}
    </button>
  );
}

// ─── Single task row (inline edit) ───────────────────────────────────────────
function TaskRow({ task, cardId, isChild = false, onUpdate, onDelete, onOpenConversation, onReload, isExpanded }) {
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);
  const [dupBusy, setDupBusy] = useState(false);
  const [tagOpen, setTagOpen] = useState(false);
  const [tagVal, setTagVal] = useState('');
  const [tagBusy, setTagBusy] = useState(false);
  const done = task.status === 'done';

  const assignMe = async () => {
    if (!user?.id) return;
    setSaving(true);
    try {
      const res = await tasksApi.assign(task.id, user.id);
      onUpdate(res.data);
      toast.success('Assigned to you');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to assign'); }
    finally { setSaving(false); }
  };

  const save = async (field, value) => {
    if (value === (task[field] || '')) return;
    setSaving(true);
    try {
      const updated = await tasksApi.update(task.id, { [field]: value });
      onUpdate(updated.data);
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const mergeDuplicate = async () => {
    if (!task.duplicate_of?.id) return;
    setDupBusy(true);
    try {
      await tasksApi.mergeDuplicate(task.id, task.duplicate_of.id);
      toast.success('Merged into original task');
      await onReload?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to merge'); }
    finally { setDupBusy(false); }
  };

  const dismissDuplicate = async () => {
    setDupBusy(true);
    try {
      await tasksApi.dismissDuplicate(task.id);
      onUpdate({ ...task, possible_duplicate: false, duplicate_of: null });
      toast.success('Dismissed duplicate flag');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to dismiss'); }
    finally { setDupBusy(false); }
  };

  const addTag = async (e) => {
    e?.preventDefault();
    const t = tagVal.trim();
    if (!t) return;
    setTagBusy(true);
    try {
      const res = await tasksApi.addTag(task.id, t);
      onUpdate({ ...task, task_tags: res.data.task_tags });
      setTagVal(''); setTagOpen(false);
      toast.success('Tag added');
    } catch { toast.error('Failed to add tag'); }
    finally { setTagBusy(false); }
  };

  const removeTag = async (tag) => {
    try {
      const res = await tasksApi.removeTag(task.id, tag);
      onUpdate({ ...task, task_tags: res.data.task_tags });
    } catch { toast.error('Failed to remove tag'); }
  };

  return (
    <div className={`tk${done ? ' done' : ''}`} data-testid="action-card-task-row"
      style={isChild ? { paddingLeft: 26 } : undefined}>
      {isChild && <span style={{ color: 'var(--faint)', alignSelf: 'flex-start', marginTop: 2, userSelect: 'none' }} aria-hidden>↳</span>}
      <div className="tk-body">
        <div className="tk-title">
          {!isChild && (
            <span className="ryg-dot" data-testid={`action-card-task-ryg-${task.id}`}
              title={RYG_META[taskRYG(task)].label}
              style={{ background: RYG_META[taskRYG(task)].color, marginRight: 8, display: 'inline-block', verticalAlign: 'middle' }} />
          )}
          {task.task_ref && (
            <span data-testid={`action-card-task-ref-${task.id}`}
              style={{ fontSize: 11, fontFamily: 'monospace', fontWeight: 600, marginRight: 6, padding: '1px 5px', borderRadius: 4, background: 'hsl(60,5%,92%)', color: '#8a8a80', verticalAlign: 'middle' }}>{task.task_ref}</span>
          )}
          <InlineField value={task.title} onSave={v => save('title', v)} placeholder="Task title" multiline />
        </div>
        <div className="tk-meta">
          <span className="mi">
            {task.assignee_name
              ? <AssigneeAvatar task={task} size={18} showName />
              : <button className="asg-empty" data-tip="Assign to me" data-testid={`action-card-task-assign-me-${task.id}`} disabled={saving} onClick={assignMe}><User /></button>}
          </span>
          <span className="sep">·</span>
          <span className="mi"><Calendar size={12} /><InlineField value={task.due_date} onSave={v => save('due_date', v)} placeholder="Due" /></span>
          <PriorityCycle value={task.priority} onSave={v => save('priority', v)} />
          {task.subtask_total > 0 && (
            <span className="text-[11px] font-semibold inline-flex items-center gap-1 px-2 py-0.5 rounded-full"
              style={{ background: 'rgba(90,125,82,0.10)', color: '#4a6b43' }}
              data-testid={`action-card-task-subtask-progress-${task.id}`}>
              {task.subtask_done || 0}/{task.subtask_total} subtasks
            </span>
          )}
          {task.possible_duplicate && (
            <Popover>
              <PopoverTrigger asChild>
                <button
                  className="text-[11px] font-semibold inline-flex items-center gap-1 px-2 py-0.5 rounded-full hover:brightness-95 transition"
                  style={{ background: 'rgba(212,164,74,0.14)', color: '#a9791f' }}
                  title={task.duplicate_of?.title ? `Similar to: ${task.duplicate_of.title}` : 'Possible duplicate task'}
                  data-testid={`action-card-task-duplicate-badge-${task.id}`}
                >
                  <Copy size={10} />Possible duplicate
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-64 bg-white border-border p-3" data-testid={`action-card-task-duplicate-popover-${task.id}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Possible duplicate</p>
                  {task.duplicate_confidence && (
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full"
                      style={task.duplicate_confidence === 'strong'
                        ? { background: 'rgba(180,51,46,0.12)', color: '#B4332E' }
                        : { background: 'rgba(212,164,74,0.16)', color: '#a9791f' }}>
                      {task.duplicate_confidence === 'strong' ? 'Strong match' : 'Likely'}
                    </span>
                  )}
                </div>
                {task.duplicate_of?.title && (
                  <p className="text-xs text-foreground mb-3 leading-snug">
                    Similar to <span className="font-medium">"{task.duplicate_of.title}"</span>
                  </p>
                )}
                <div className="flex flex-col gap-1.5">
                  <button
                    onClick={mergeDuplicate}
                    disabled={dupBusy}
                    data-testid={`action-card-task-merge-button-${task.id}`}
                    className="w-full inline-flex items-center justify-center gap-1.5 h-8 rounded-lg text-xs font-semibold text-white disabled:opacity-50"
                    style={{ background: '#6994c6' }}
                  >
                    <GitMerge size={13} />Merge into original
                  </button>
                  <button
                    onClick={dismissDuplicate}
                    disabled={dupBusy}
                    data-testid={`action-card-task-dismiss-duplicate-button-${task.id}`}
                    className="w-full inline-flex items-center justify-center gap-1.5 h-8 rounded-lg text-xs font-medium border border-border hover:bg-secondary disabled:opacity-50"
                  >
                    <X size={13} />Dismiss
                  </button>
                </div>
              </PopoverContent>
            </Popover>
          )}
          {(task.task_tags || []).map(tg => (
            <span key={tg} className="text-[11px] font-medium inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full border"
              style={{ background: 'rgba(90,125,82,0.08)', color: '#4a6b43', borderColor: 'rgba(90,125,82,0.35)' }}
              data-testid={`action-card-task-tasktag-${task.id}-${tg}`}>
              <Hash size={9} />{tg}
              <button onClick={() => removeTag(tg)} className="ml-0.5 rounded-full hover:bg-black/10 p-0.5" title="Remove tag"
                data-testid={`action-card-task-remove-tag-${task.id}-${tg}`}>
                <X size={9} />
              </button>
            </span>
          ))}
        </div>
        <div className="tk-status-row" style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 7 }}>
          <span style={{ fontSize: 12, color: 'var(--mute)', fontWeight: 600 }}>Status:</span>
          <Select value={task.status === 'todo' ? 'not_started' : (task.status || 'not_started')} onValueChange={v => save('status', v)}>
            <SelectTrigger data-testid="action-card-task-status-select" className="h-7 w-[140px] text-xs bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="not_started" data-testid="action-card-task-status-not_started">Not started</SelectItem>
              <SelectItem value="in_progress" data-testid="action-card-task-status-in_progress">In progress</SelectItem>
              <SelectItem value="done" data-testid="action-card-task-status-done">Completed</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <Popover open={tagOpen} onOpenChange={setTagOpen}>
        <PopoverTrigger asChild>
          <button className="tk-del" title="Add a tag to this task" data-testid={`action-card-task-add-tag-${task.id}`} style={{ color: '#5a7d52' }}>
            <Hash size={14} />
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-56 bg-white border-border p-2.5" data-testid={`action-card-task-add-tag-popover-${task.id}`}>
          <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Add task tag</p>
          <form onSubmit={addTag} className="flex gap-1.5">
            <input autoFocus value={tagVal} onChange={e => setTagVal(e.target.value)} placeholder="e.g. urgent"
              data-testid={`action-card-task-add-tag-input-${task.id}`}
              className="flex-1 min-w-0 rounded-lg border border-border px-2 py-1 text-sm outline-none focus:border-[#5a7d52]" style={{ fontSize: '16px' }} />
            <button type="submit" disabled={tagBusy || !tagVal.trim()} data-testid={`action-card-task-add-tag-submit-${task.id}`}
              className="inline-flex items-center justify-center px-2.5 rounded-lg text-white disabled:opacity-50" style={{ background: '#5a7d52' }}>
              {tagBusy ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
            </button>
          </form>
        </PopoverContent>
      </Popover>
      <button className="tk-del" onClick={() => onOpenConversation?.(task)} title="Edit inline — status, assignee, notes & files" data-testid="action-card-task-conversation" style={{ color: isExpanded ? '#3d6a9e' : '#6994c6' }}><MessageSquare size={14} /></button>
      <button className="tk-del" onClick={() => onDelete(task.id)} title="Delete task"><Trash2 size={13} /></button>
    </div>
  );
}

// ─── Add task row ─────────────────────────────────────────────────────────────
function AddTaskRow({ cardId, onAdded }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: '', owner: 'Me', due_date: '', priority: 'medium' });
  const [saving, setSaving] = useState(false);
  const titleRef = useRef(null);

  useEffect(() => {
    if (open) setTimeout(() => titleRef.current?.focus(), 50);
  }, [open]);

  const submit = async (e) => {
    e?.preventDefault();
    if (!form.title.trim()) { titleRef.current?.focus(); return; }
    setSaving(true);
    try {
      const res = await actionCardsApi.addTask(cardId, form);
      onAdded(res.data);
      setForm({ title: '', owner: 'Me', due_date: '', priority: 'medium' });
      setOpen(false);
      toast.success('Task added');
    } catch { toast.error('Failed to add task'); }
    finally { setSaving(false); }
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="addtask-trigger" data-testid="action-card-add-task-button">
        <Plus size={15} />Add task
      </button>
    );
  }

  return (
    <form onSubmit={submit} className="addtask-form">
      <input
        ref={titleRef}
        className="at-title"
        value={form.title}
        onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
        onKeyDown={e => { if (e.key === 'Escape') setOpen(false); }}
        placeholder="Task title…"
        data-testid="addtask-title-input"
      />
      <div className="at-meta">
        <input value={form.due_date} onChange={e => setForm(p => ({ ...p, due_date: e.target.value }))} placeholder="Due date (e.g. Fri EOD)" data-testid="addtask-due-input" />
        <select value={form.priority} onChange={e => setForm(p => ({ ...p, priority: e.target.value }))} data-testid="addtask-priority-select">
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
      </div>
      <div className="at-actions">
        <button type="submit" disabled={saving || !form.title.trim()} className="btn btn-primary" data-testid="action-card-save-task-button">
          {saving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}Add task
        </button>
        <button type="button" onClick={() => setOpen(false)} className="btn btn-ghost">Cancel</button>
      </div>
    </form>
  );
}

function BulletList({ items, onSave, placeholder, marker = 'dot' }) {
  const [editingIdx, setEditingIdx] = useState(null);
  const [drafts, setDrafts] = useState([...( items || [])]);

  useEffect(() => { setDrafts([...(items || [])]); }, [items]);

  const saveAll = (nd) => {
    const filtered = nd.filter(Boolean);
    setDrafts(filtered);
    onSave(filtered);
  };

  const addItem = () => {
    setDrafts(p => [...p, '']);
    setEditingIdx(drafts.length);
  };

  if (!items || items.length === 0) {
    return (
      <button onClick={addItem} className="ed-add">
        <Plus size={14} />Add {placeholder || 'item'}
      </button>
    );
  }

  const mk = marker === 'dia' ? 'em-dia' : 'em-dot';

  return (
    <ul className="edl">
      {drafts.map((item, idx) => (
        <li key={idx} className="eli">
          <span className={mk} />
          {editingIdx === idx ? (
            <input
              autoFocus
              className="ed-input"
              value={item}
              onChange={e => { const nd = [...drafts]; nd[idx] = e.target.value; setDrafts(nd); }}
              onBlur={() => { setEditingIdx(null); saveAll(drafts); }}
              onKeyDown={e => {
                if (e.key === 'Enter') { setEditingIdx(null); saveAll(drafts); }
                if (e.key === 'Escape') { setEditingIdx(null); setDrafts([...(items || [])]); }
              }}
            />
          ) : (
            <span className="etx" onClick={() => setEditingIdx(idx)}>{item}</span>
          )}
          <button className="ed-del" onClick={() => saveAll(drafts.filter((_, i) => i !== idx))}>
            <Trash2 size={12} />
          </button>
        </li>
      ))}
      <li>
        <button onClick={addItem} className="ed-add"><Plus size={14} />Add {placeholder || 'item'}</button>
      </li>
    </ul>
  );
}

// ─── Editable card heading (rename) ──────────────────────────────────────────
function EditableHeading({ value, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const ref = useRef(null);
  useEffect(() => { if (editing) { ref.current?.focus(); ref.current?.select(); } }, [editing]);
  const save = () => {
    setEditing(false);
    const v = draft.trim();
    if (v && v !== value) onSave(v); else setDraft(value || '');
  };
  if (editing) {
    return (
      <input
        ref={ref}
        className="ac-title ac-title-edit"
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={save}
        onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') { setEditing(false); setDraft(value || ''); } }}
        data-testid="action-card-title-input"
      />
    );
  }
  return (
    <h1 className="ac-title ac-title-editable" data-testid="action-card-title" title="Click to rename"
      onClick={() => { setDraft(value || ''); setEditing(true); }}>
      {value || 'Untitled'}<Edit2 size={15} className="ac-title-pencil" />
    </h1>
  );
}

// ─── Tag editor (add / remove) ───────────────────────────────────────────────
function TagEditor({ tags, onSave }) {
  const [adding, setAdding] = useState(false);
  const [val, setVal] = useState('');
  const list = tags || [];
  const add = () => {
    const c = val.trim();
    if (c && !list.includes(c)) onSave([...list, c]);
    setVal(''); setAdding(false);
  };
  return (
    <div className="ac-tags" data-testid="action-card-tags">
      {list.map(t => (
        <span key={t} className="ac-tag-chip">
          <Tag size={11} />{t}
          <button className="x" onClick={() => onSave(list.filter(x => x !== t))} aria-label={`Remove ${t}`} data-testid={`action-card-tag-remove-${t}`}><X size={11} /></button>
        </span>
      ))}
      {adding ? (
        <input autoFocus className="ac-tag-input" value={val}
          onChange={e => setVal(e.target.value)} onBlur={add}
          onKeyDown={e => { if (e.key === 'Enter') add(); if (e.key === 'Escape') { setVal(''); setAdding(false); } }}
          placeholder="Tag name" data-testid="action-card-tag-input" />
      ) : (
        <button className="ac-tag-add" onClick={() => setAdding(true)} data-testid="action-card-add-tag-button"><Plus size={12} />Add tag</button>
      )}
    </div>
  );
}

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Shared-thread message (owner view) ──────────────────────────────────────
function ThreadMsg({ r }) {
  const owner = r.is_owner;
  const initial = (r.sender_name || '?').slice(0, 1).toUpperCase();
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }} data-testid="conversation-message">
      <div style={{
        width: 28, height: 28, borderRadius: '50%', flex: 'none', fontSize: 11, fontWeight: 700,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: owner ? 'var(--blue)' : 'var(--paper2)', color: owner ? '#fff' : 'var(--mute)',
        border: owner ? 'none' : '1px solid var(--line)',
      }}>{initial}</div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--ink)' }}>{r.sender_name}{owner ? ' · you' : ''}</span>
          <span style={{ fontSize: 10.5, color: 'var(--faint)' }}>
            {new Date(r.timestamp).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
          </span>
        </div>
        <div style={{
          fontSize: 13.5, marginTop: 4, borderRadius: 12, padding: '8px 12px', whiteSpace: 'pre-wrap',
          background: owner ? 'var(--tint)' : 'var(--paper2)', color: 'var(--ink)', border: '1px solid var(--line)',
        }}>{r.message}</div>
      </div>
    </div>
  );
}

export default function ActionCardPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { openTaskDrawer } = useTaskDrawer();
  const [card, setCard] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [expandedTaskId, setExpandedTaskId] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [thread, setThread] = useState([]);
  const [threadShared, setThreadShared] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [loading, setLoading] = useState(true);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [timelineExpanded, setTimelineExpanded] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [orgTeams, setOrgTeams] = useState([]);
  const [orgAdmin, setOrgAdmin] = useState(false);
  const [workspaces, setWorkspaces] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [taskRygFilter, setTaskRygFilter] = useState(null);

  useEffect(() => {
    templatesApi.list().then(res => setTemplates(res.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    organizationApi.get().then(res => {
      if (res.data.company) {
        setOrgAdmin(res.data.global_role === 'admin');
        organizationApi.teams().then(t => setOrgTeams(t.data.teams || [])).catch(() => {});
      }
    }).catch(() => {});
    teamsApi.list().then(r => setWorkspaces(r.data.teams || [])).catch(() => {});
  }, []);

  // Personal/Team + workspace selector (mirrors the capture page). Controls the card's
  // workspace membership (team_id) via move, and keeps visibility_scope in sync.
  const setCardScope = async (mode, wsId) => {
    try {
      if (mode === 'personal') {
        await actionCardsApi.move(id, null, null);
        await organizationApi.assignCardTeam(id, { visibility_scope: 'private' });
      } else {
        const tid = wsId || workspaces[0]?.id;
        if (!tid) { toast.error('Create a workspace first'); return; }
        await actionCardsApi.move(id, null, tid);
        await organizationApi.assignCardTeam(id, { visibility_scope: 'team' });
      }
      await load();
      toast.success('Saved', { duration: 1500 });
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not update'); }
  };

  const assignOrgTeam = async (payload) => {
    try {
      const res = await organizationApi.assignCardTeam(id, payload);
      setCard(res.data);
      toast.success('Saved', { duration: 1500 });
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to save'); }
  };

  const load = useCallback(async () => {
    try {
      const [cardRes, tasksRes, contactsRes] = await Promise.all([
        actionCardsApi.get(id),
        tasksApi.list({}),
        contactsApi.list({}),
      ]);
      setCard(cardRes.data);
      setTasks((tasksRes.data.tasks || []).filter(t => t.action_card_id === id));
      setContacts(contactsRes.data.contacts || []);
      axios.get(`${API_BASE}/action-cards/${id}/share-thread`)
        .then(r => { setThread(r.data.responses || []); setThreadShared(!!r.data.shared); })
        .catch(() => {});
    } catch {
      toast.error('Failed to load Action Card');
      navigate('/');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const updateField = async (field, value) => {
    try {
      const res = await actionCardsApi.update(id, { [field]: value });
      setCard(res.data);
      toast.success('Saved', { duration: 1500 });
    } catch { toast.error('Failed to save'); }
  };
  // Renaming the heading must keep card.title in sync so the list view reflects it.
  const saveHeading = async (field, value) => {
    const patch = field === 'title' ? { title: value } : { [field]: value, title: value };
    try {
      const res = await actionCardsApi.update(id, patch);
      setCard(res.data);
      toast.success('Saved', { duration: 1500 });
    } catch { toast.error('Failed to save'); }
  };
  const regenerateNotes = async (templateId) => {
    if (regenerating) return;
    const prev = {
      summary_note: card.summary_note || '',
      summary_bullets: card.summary_bullets || [],
      decisions: card.decisions || [],
    };
    setRegenerating(true);
    try {
      const res = await actionCardsApi.regenerateNotes(id, templateId);
      setCard(res.data);
      toast.success(templateId ? 'Notes reformatted with template' : 'Notes regenerated', {
        action: {
          label: 'Undo',
          onClick: async () => {
            try {
              const restored = await actionCardsApi.update(id, prev);
              setCard(restored.data);
              toast.success('Previous notes restored');
            } catch { toast.error('Failed to restore notes'); }
          },
        },
        duration: 10000,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to regenerate notes');
    } finally { setRegenerating(false); }
  };


  const toggleMute = async () => {
    const next = !card?.muted;
    try {
      if (next) await actionCardsApi.mute(id); else await actionCardsApi.unmute(id);
      setCard(c => ({ ...c, muted: next }));
      toast.success(next ? 'Notifications muted for this card' : 'Notifications unmuted');
    } catch { toast.error('Failed to update notifications'); }
  };

  // Save a rich note + keep the legacy bullet array in sync for downstream consumers.
  const saveNote = async (noteField, bulletsField, html) => {
    try {
      const res = await actionCardsApi.update(id, { [noteField]: html, [bulletsField]: htmlToBullets(html) });
      setCard(res.data);
      toast.success('Saved', { duration: 1500 });
    } catch { toast.error('Failed to save'); }
  };

  const openContact = async (name) => {
    if (!isRealPerson(name)) return;
    const key = (name || '').trim().toLowerCase();
    if (!key) return;
    let match = contacts.find(c => (c.name || '').trim().toLowerCase() === key);
    if (!match) {
      try {
        const res = await contactsApi.create({ name: name.trim() });
        match = res.data;
        setContacts(prev => [...prev, match]);
      } catch { toast.error('Could not open contact'); return; }
    }
    navigate(`/contacts/${match.id}`);
  };

  const sendReply = async () => {
    const msg = replyText.trim();
    if (!msg) return;
    setSendingReply(true);
    try {
      const res = await axios.post(`${API_BASE}/action-cards/${id}/share-thread`, { message: msg });
      setThread(prev => [...prev, res.data.response]);
      setReplyText('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send reply');
    } finally {
      setSendingReply(false);
    }
  };

  const handleTaskComplete = async (taskId, currentStatus) => {
    const newStatus = currentStatus === 'done' ? 'not_started' : 'done';
    try {
      const res = await tasksApi.update(taskId, { status: newStatus });
      setTasks(prev => prev.map(t => t.id === taskId ? res.data : t));
      if (newStatus === 'done') toast.success('Task completed!');
    } catch { toast.error('Failed to update task'); }
  };

  const handleTaskUpdate = (updatedTask) => {
    setTasks(prev => prev.map(t => t.id === updatedTask.id ? updatedTask : t));
  };

  const handleTaskDelete = async (taskId) => {
    if (!window.confirm('Delete this task?')) return;
    try {
      await actionCardsApi.deleteTask(id, taskId);
      setTasks(prev => prev.filter(t => t.id !== taskId));
      toast.success('Task deleted');
    } catch { toast.error('Failed to delete task'); }
  };

  const handleTaskAdded = (newTask) => {
    setTasks(prev => [...prev, newTask]);
  };

  const handleDeleteCard = async () => {
    if (!window.confirm('Delete this Action Card?')) return;
    try {
      await actionCardsApi.delete(id);
      toast.success('Deleted');
      navigate('/');
    } catch { toast.error('Failed to delete'); }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      const res = await actionCardsApi.update(id, { status: newStatus });
      setCard(res.data);
      toast.success(`Status: ${newStatus}`);
    } catch { toast.error('Failed'); }
  };

  if (loading) {
    return (
      <div className="px-5 md:px-10 py-6 space-y-4">
        <Skeleton className="h-8 w-48 rounded-xl" />
        <Skeleton className="h-24 w-full rounded-xl" />
        <Skeleton className="h-40 w-full rounded-xl" />
      </div>
    );
  }

  if (!card) return null;

  // Group subtasks under their parent so the card view mirrors the Tasks page hierarchy.
  const topLevelTasks = tasks.filter(t => !t.parent_task_id);
  const childrenByParent = {};
  tasks.forEach(t => { if (t.parent_task_id) (childrenByParent[t.parent_task_id] = childrenByParent[t.parent_task_id] || []).push(t); });
  const pendingTasks = topLevelTasks.filter(t => t.status !== 'done');
  const completedTasks = topLevelTasks.filter(t => t.status === 'done');
  const orderedTasks = [];
  [...pendingTasks, ...completedTasks].forEach(t => {
    orderedTasks.push(t);
    (childrenByParent[t.id] || []).forEach(c => orderedTasks.push({ ...c, _isChild: true }));
  });
  // Orphan subtasks whose parent isn't attached to this card.
  tasks.forEach(t => {
    if (t.parent_task_id && !topLevelTasks.some(p => p.id === t.parent_task_id)) orderedTasks.push({ ...t, _isChild: true });
  });
  const doneCount = tasks.filter(t => t.status === 'done').length;

  // RYG roll-up for this card (drill-down: card status = aggregate of its tasks).
  const _today = todayStr();
  const cardRygCounts = rygFromTasks(topLevelTasks, _today);
  const cardRYG = topLevelTasks.length ? aggregateRYG(cardRygCounts) : null;
  const _matchTopIds = taskRygFilter
    ? new Set(topLevelTasks.filter(t => taskRYG(t, _today) === taskRygFilter).map(t => t.id))
    : null;
  const visibleOrderedTasks = taskRygFilter
    ? orderedTasks.filter(t => t._isChild ? _matchTopIds.has(t.parent_task_id) : _matchTopIds.has(t.id))
    : orderedTasks;

  const createdStr = new Date(card.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  // Meeting cards are titled by topic/meeting name; the contact stays the real person.
  const isMeeting = card.source === 'meeting';
  const contactIsPerson = isRealPerson(card.contact_name);
  const useTitleHeading = (isMeeting || !contactIsPerson) && card.title && card.title.toLowerCase() !== (card.contact_name || '').toLowerCase();
  const headingValue = useTitleHeading ? card.title : (contactIsPerson ? card.contact_name : (card.title || 'Action Card'));
  const headingField = useTitleHeading ? 'title' : (contactIsPerson ? 'contact_name' : 'title');

  // Derive participants from real data: You + the contact (+ any diarized speakers), deduped
  const people = [];
  const seen = new Set();
  const addPerson = (name, role) => {
    const key = (name || '').trim().toLowerCase();
    if (!key || seen.has(key) || !isRealPerson(name)) return;
    seen.add(key);
    people.push({ name: name.trim(), role });
  };
  // Whether the card lists real, named people in its participants array.
  const hasNamedParticipants = Array.isArray(card.participants)
    && card.participants.some(p => isRealPerson(typeof p === 'string' ? p : p?.name));
  if (user?.name) addPerson(user.name, 'You');
  // Skip contact_name when it's just the card's heading/topic AND named people already exist —
  // otherwise the Action Card's own title shows up as a bogus participant.
  if (card.contact_name && !(headingField === 'contact_name' && hasNamedParticipants)) {
    addPerson(card.contact_name, card.contact_company || card.contact_email || 'Participant');
  }
  if (Array.isArray(card.participants)) {
    card.participants.forEach(p => {
      const name = typeof p === 'string' ? p : p?.name;
      const role = (typeof p === 'object' && p?.role && p.role !== 'recorder' && p.role !== 'contact') ? p.role : 'Participant';
      if (name) addPerson(name, role);
    });
  }

  const confidencePct = typeof card.confidence === 'number'
    ? `${Math.round(card.confidence <= 1 ? card.confidence * 100 : card.confidence)}% confidence`
    : null;

  // Map diarized speaker labels (A/B/C) to real names: recorder -> You, others -> contact names
  const speakerMap = {};
  if (Array.isArray(card.participants)) {
    card.participants.forEach(p => {
      if (!p || !p.speaker_label) return;
      if (p.role === 'recorder') speakerMap[p.speaker_label] = user?.name ? user.name.split(' ')[0] : 'You';
      else if (p.name) speakerMap[p.speaker_label] = p.name;
    });
  }
  const speakerLabel = (letter) => (letter ? (speakerMap[letter] || `Speaker ${letter}`) : 'Conversation');

  return (
    <div className="ac-v2" data-testid="action-card-page">
      <button className="back" onClick={() => navigate(-1)} data-testid="action-card-back">
        <ArrowLeft size={15} />Action Cards
      </button>

      {/* Header */}
      <div className="card-head">
        <div className="ch-left">
          <div className="ch-title-row">
            <div className={`type-ic ${card.source === 'audio' ? 'green' : ''}`} data-tip={card.source === 'audio' ? 'Audio capture' : 'Text capture'}>
              {card.source === 'audio' ? <Mic size={16} /> : <FileText size={16} />}
            </div>
            <EditableHeading value={headingValue} onSave={v => saveHeading(headingField, v)} />
          </div>
          {useTitleHeading && contactIsPerson && (
            <div className="ch-subhead" data-testid="action-card-subhead">with {card.contact_name}</div>
          )}
          <div className="ch-meta">
            <span className={`st-pill ${card.status === 'completed' ? 'done' : ''}`} data-testid="action-card-status">
              {card.status === 'completed' ? 'Completed' : card.status === 'archived' ? 'Archived' : 'Active'}
            </span>
            {cardRYG && (
              <span className="ryg-pill" data-testid="action-card-ryg"
                title={`${RYG_META[cardRYG].label} — ${cardRygCounts.red} overdue · ${cardRygCounts.yellow} on track · ${cardRygCounts.green} completed`}
                style={{ color: RYG_META[cardRYG].color, background: RYG_META[cardRYG].bg }}>
                <span className="ryg-dot" style={{ background: RYG_META[cardRYG].color }} />{RYG_META[cardRYG].label}
              </span>
            )}
            {confidencePct && <span className="conf-pill" data-tip="Milk's confidence in this card">{confidencePct}</span>}
            <span>{createdStr}</span>
            {card.source && <><span>·</span><span style={{ textTransform: 'capitalize' }}>{card.source === 'audio' ? 'Recorded audio' : card.source}</span></>}
            {card.duration && <><span>·</span><span>{card.duration}</span></>}
          </div>
        </div>
        <div className="ch-actions">
          <ParticipantStack people={people} participants={card.participants} userName={user?.name} onSave={v => updateField('participants', v)} onOpenContact={openContact} />
          <button className="btn-primary" data-testid="action-card-primary-cta-button" onClick={() => setEmailModalOpen(true)}>
            <Mail size={15} />Generate Email
          </button>
          <button className="btn-ghost" onClick={() => setShareModalOpen(true)} data-testid="action-card-share-button">
            <Share2 size={15} />Share
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="icbtn" aria-label="More" data-testid="action-card-more-button"><MoreHorizontal size={18} /></button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="border-border bg-white">
              <DropdownMenuItem onClick={() => setPdfDialogOpen(true)} className="text-sm" data-testid="action-card-export-pdf">
                <Download size={13} className="mr-2" />Export PDF
              </DropdownMenuItem>
              <DropdownMenuItem onClick={toggleMute} className="text-sm" data-testid="action-card-mute-toggle">
                {card.muted ? <><Bell size={13} className="mr-2" />Unmute notifications</> : <><BellOff size={13} className="mr-2" />Mute notifications</>}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => handleStatusChange('completed')} className="text-sm">
                <Check size={13} className="mr-2" />Mark Completed
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleStatusChange('archived')} className="text-sm">
                <Tag size={13} className="mr-2" />Archive
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleDeleteCard} className="text-destructive text-sm">
                <Trash2 size={13} className="mr-2" />Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Tags + workspace */}
      <div className="chips" data-testid="card-team-visibility">
        {(card.user_id === user?.id || orgAdmin) && (
          <FilterSelect
            testid="card-workspace-select"
            icon={Users}
            placeholder="Workspace"
            value={card.team_id || ''}
            options={workspaces.map(t => ({ value: t.id, label: t.name }))}
            onChange={v => setCardScope(v ? 'team' : 'personal', v || null)}
          />
        )}
        {(card.user_id === user?.id || orgAdmin) && orgTeams.length > 0 && (
          <FilterSelect
            testid="card-org-team-select"
            icon={Users}
            placeholder="No org team"
            value={card.org_team_id || ''}
            options={orgTeams.map(t => ({ value: t.id, label: t.name }))}
            onChange={v => assignOrgTeam({ org_team_id: v })}
          />
        )}
        <TagEditor tags={card.tags} onSave={v => updateField('tags', v)} />
      </div>

      {/* Location trail — where the card lives and who moved it last */}
      {(() => {
        const mv = [...(card.timeline || [])].reverse().find(e => e.event === 'moved');
        return mv ? (
          <div className="card-move-note" data-testid="card-location-trail">
            <MapPin size={12} />{mv.detail} · {relTime(mv.timestamp)}
          </div>
        ) : null;
      })()}

      <div className="layout" data-testid="action-card-body">
        <div className="col-main" data-testid="action-card-main-col">

      {/* Overview: summary + decisions */}
      <div className="blk">
        <div className="blk-h">
          <span className="lab">Summary</span>
          {card.applied_template_name && (
            <span data-testid="applied-template-badge" className="tpl-badge">
              <LayoutTemplate size={10} />{card.applied_template_name}
            </span>
          )}
          {(card.transcript || card.raw_input) && (
            <div className="right">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button disabled={regenerating} data-testid="action-card-apply-template-trigger" className="btn-mini" title="Reformat these notes using a template">
                    <LayoutTemplate size={12} />Apply template
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64 max-h-80 overflow-y-auto">
                  <DropdownMenuLabel className="text-xs">Reformat notes with…</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {templates.length === 0 && (
                    <div className="px-2 py-3 text-xs text-muted-foreground">No templates yet. Create one in Settings → Templates.</div>
                  )}
                  {templates.map(t => (
                    <DropdownMenuItem key={t.id} data-testid={`apply-template-${t.id}`} onClick={() => regenerateNotes(t.id)} className="text-sm flex-col items-start gap-0.5">
                      <span className="font-medium">{t.name}</span>
                      <span className="text-[11px] text-muted-foreground">
                        {t.is_builtin ? 'Built-in' : (t.visibility === 'team' ? 'Shared with workspace' : 'Private')} · {(t.sections || []).length} sections
                      </span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              <button onClick={() => regenerateNotes()} disabled={regenerating} data-testid="action-card-regenerate-notes" className="btn-mini" title="Re-run AI to regenerate structured notes from the transcript">
                {regenerating ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                {regenerating ? 'Working…' : 'Regenerate'}
              </button>
            </div>
          )}
        </div>
        <div className="blk-body" data-testid="action-card-summary-section">
          <RichNote
            value={card.summary_note || bulletsToHtml(card.summary_bullets)}
            onSave={html => saveNote('summary_note', 'summary_bullets', html)}
            placeholder="Add a summary — use bullets, headings or formatting…"
            testid="action-card-summary-editor"
          />
        </div>
        <div className="blk-foot">
          <Sparkles size={12} />Poured by <b>Milk</b> from your {card.source === 'audio' ? 'recording' : 'text capture'} · edits train it
        </div>
      </div>

      <div className="blk">
        <div className="blk-h"><span className="lab">Decisions</span></div>
        <div className="blk-body" data-testid="action-card-decisions-section">
          <RichNote
            value={card.decisions_note || bulletsToHtml(card.decisions)}
            onSave={html => saveNote('decisions_note', 'decisions', html)}
            placeholder="Capture key decisions…"
            testid="action-card-decisions-editor"
          />
        </div>
        <div className="blk-foot">
          <Check size={12} />Decisions become the record — <b>everyone shared on this card sees them</b>
        </div>
      </div>


      {/* Highlights — from AssemblyAI auto-highlights (diarized audio only) */}
      {Array.isArray(card.highlights) && card.highlights.length > 0 && (
        <div className="sec">
          <div className="sec-h">
            <span className="lab">Highlights</span>
            <span className="aibadge"><Zap size={12} fill="currentColor" />Auto-detected</span>
          </div>
          <section className="card" data-testid="action-card-highlights">
            {card.highlights.map((h, i) => {
              const mins = h.start_ms != null ? Math.floor(h.start_ms / 60000) : null;
              const secs = h.start_ms != null ? Math.floor((h.start_ms % 60000) / 1000) : null;
              const time = mins != null ? `${mins}:${String(secs).padStart(2, '0')}` : '—';
              const speaker = speakerLabel(h.speaker);
              return (
                <div className="hl" key={i}>
                  <span className="time">{time}</span>
                  <div className="htext">
                    <div className="hq">“{h.text}”</div>
                    <div className="hsp">{speaker}{h.count > 1 ? ` · mentioned ${h.count}×` : ''}</div>
                  </div>
                </div>
              );
            })}
          </section>
        </div>
      )}

      {/* Transcript */}
      {card.transcript && (
        <div className="sec">
          <div className="sec-h"><span className="lab">Transcript</span></div>
          <TranscriptViewer transcript={card.transcript} speakerMap={speakerMap} />
        </div>
      )}

        </div>
        <div className="col-rail" data-testid="action-card-rail-col">

      {/* Tasks */}
      <div className="rail-card">
        <div className="rail-h">
          <span className="lab">Tasks</span>
          <span className="right"><b>{doneCount}</b> of {tasks.length} completed</span>
        </div>
        {topLevelTasks.length > 0 && (
          <div className="ryg-bar rail" data-testid="action-card-ryg-bar" style={{ padding: '0 16px 8px', marginBottom: 0 }}>
            {RYG_ORDER.map(k => (
              <button key={k} className={`ryg-chip${taskRygFilter === k ? ' on' : ''}`} data-testid={`action-card-ryg-${k}`}
                onClick={() => setTaskRygFilter(taskRygFilter === k ? null : k)}
                title={taskRygFilter === k ? 'Clear filter' : `Show ${RYG_META[k].label.toLowerCase()} tasks`}
                style={taskRygFilter === k ? { borderColor: RYG_META[k].color, background: RYG_META[k].bg } : undefined}>
                <span className="ryg-dot" style={{ background: RYG_META[k].color }} />
                {RYG_META[k].label}<b>{cardRygCounts[k]}</b>
              </button>
            ))}
            {taskRygFilter && <button className="ryg-clear" data-testid="action-card-ryg-clear" onClick={() => setTaskRygFilter(null)}><X size={13} />Clear</button>}
          </div>
        )}
        <div data-testid="action-card-action-items-list">
          {tasks.length === 0 && (
            <p style={{ fontSize: 13, color: 'var(--mute)', fontStyle: 'italic', padding: '10px 16px' }}>No action items yet.</p>
          )}
          <div className="divide-y divide-border/40">
            {visibleOrderedTasks.map(task => (
              <div key={task.id} data-testid={`action-card-task-container-${task.id}`}>
                <TaskRow task={task} cardId={id} isChild={task._isChild} onUpdate={handleTaskUpdate} onDelete={handleTaskDelete}
                  onOpenConversation={t => openTaskDrawer(t.id, { onChanged: load })}
                  onReload={load} isExpanded={false} />
              </div>
            ))}
          </div>
          <AddTaskRow cardId={id} onAdded={handleTaskAdded} />
        </div>
      </div>

      {/* Suggested next steps — real, functional actions */}
      <div className="rail-card">
        <div className="rail-h">
          <span className="lab">Suggested next steps</span>
          <span className="milk-badge"><Zap size={11} fill="currentColor" />Milk</span>
        </div>
        <div data-testid="action-card-next-steps">
          <div className="nstep">
            <span className="ns-ic"><Mail size={15} /></span>
            <div className="ns-body">
              <div className="ns-t">Send the recap email{contactIsPerson ? ` to ${card.contact_name}` : ''}</div>
              <div className="ns-s">Draft ready · also schedules your next follow-up</div>
            </div>
            <button className="ns-btn" onClick={() => setEmailModalOpen(true)} data-testid="next-step-email">
              Review<ArrowRight size={12} />
            </button>
          </div>
          <div className="nstep">
            <span className="ns-ic green"><Share2 size={15} /></span>
            <div className="ns-body">
              <div className="ns-t">Share this Action Card with your team</div>
              <div className="ns-s">Generate a secure, read-only link</div>
            </div>
            <button className="ns-btn ghost" onClick={() => setShareModalOpen(true)} data-testid="next-step-share">Share</button>
          </div>
          {pendingTasks.length > 0 && (
            <div className="nstep">
              <span className="ns-ic butter"><Check size={15} /></span>
              <div className="ns-body">
                <div className="ns-t">Knock out {pendingTasks.length} open task{pendingTasks.length !== 1 ? 's' : ''}</div>
                <div className="ns-s">Stay on top of every follow-up from this conversation</div>
              </div>
              <button className="ns-btn ghost" onClick={() => navigate('/tasks')} data-testid="next-step-tasks">View tasks</button>
            </div>
          )}
        </div>
      </div>

      {/* Conversation (shared thread) */}
      {(threadShared || thread.length > 0) && (
        <div className="rail-card" data-testid="action-card-conversation">
          <div className="rail-h"><span className="lab">Conversation</span><span className="right">{thread.length}</span></div>
          <div className="rail-body">
              {thread.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {thread.map(r => <ThreadMsg key={r.id} r={r} />)}
                </div>
              ) : (
                <p style={{ fontSize: 13, color: 'var(--mute)' }}>No replies yet. People you’ve shared this with can reply here.</p>
              )}
              <div style={{ marginTop: thread.length > 0 ? 14 : 10, borderTop: thread.length > 0 ? '1px solid var(--line)' : 'none', paddingTop: thread.length > 0 ? 12 : 0 }}>
                <textarea
                  value={replyText}
                  onChange={e => setReplyText(e.target.value)}
                  placeholder="Reply to the thread…"
                  data-testid="conversation-reply-input"
                  rows={2}
                  style={{ width: '100%', resize: 'none', borderRadius: 12, padding: '9px 12px', fontSize: 13.5, color: 'var(--ink)', background: 'var(--paper2)', border: '1px solid var(--line)', outline: 'none', fontFamily: 'inherit' }}
                />
                <button
                  onClick={sendReply}
                  disabled={sendingReply || !replyText.trim()}
                  data-testid="conversation-reply-send"
                  className="ce-gen"
                  style={{ marginTop: 9, opacity: (sendingReply || !replyText.trim()) ? 0.5 : 1 }}
                >
                  {sendingReply ? 'Sending…' : 'Reply'}
                </button>
              </div>
            </div>
        </div>
      )}

      {/* Activity */}
      <div className="rail-card">
        <div className="rail-h">
          <span className="lab">Activity</span>
          <span className="sec-link" onClick={() => setTimelineExpanded(p => !p)}>{timelineExpanded ? 'Collapse' : 'Expand'}</span>
        </div>
        <div className="rail-body" data-testid="action-card-timeline">
          {timelineExpanded ? (
            <ActivityTimeline events={card.timeline} loading={false} />
          ) : (
            <p style={{ fontSize: 13, color: 'var(--mute)' }}>{card.timeline?.length || 0} events — click expand to view.</p>
          )}
        </div>
      </div>

      {/* Emails sent */}
      {card.emails_sent?.length > 0 && (
        <div className="rail-card">
          <div className="rail-h"><span className="lab">Emails sent</span><span className="right">{card.emails_sent.length}</span></div>
          <div className="rail-body" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {card.emails_sent.map((email, i) => (
              <div key={i} style={{ fontSize: 12.5, padding: '10px 12px', borderRadius: 11, background: 'var(--paper2)', border: '1px solid var(--line)' }}>
                <p style={{ fontWeight: 700, color: 'var(--ink)' }}>{email.subject}</p>
                <p style={{ color: 'var(--faint)', marginTop: 3 }}>
                  {new Date(email.sent_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

        </div>
      </div>

      <EmailModal open={emailModalOpen} onClose={() => { setEmailModalOpen(false); load(); }} actionCard={card} />
      <ShareModal open={shareModalOpen} onClose={() => setShareModalOpen(false)} cardId={id} />

      <ExportPdfDialog open={pdfDialogOpen} onOpenChange={setPdfDialogOpen} card={card} tasks={tasks} />
    </div>
  );
}
